import { type TFunction } from 'i18next';
import { isNil } from 'lodash';
import moment from 'moment';

import {
  type AtlasGqlHorizonDamage,
  type AtlasGqlHorizonDamagePredictedPropagation,
  type AtlasGqlHorizonDamagePropagationEstimate,
  AtlasGqlHorizonDamagePredictedPropagationConfidence,
} from 'types/atlas-graphql';
import { formatMonthYear } from 'utils/format';

// Severities run 1-5 with Critical as a separate flag, but the propagation model
// treats Critical as a sixth level (`severity6`).
export const CRITICAL_SEVERITY_LEVEL = 6;

// The forecast the card pill reports on: "...within 12 months".
export const PILL_FORECAST_YEARS = 1;

export const PROPAGATION_SORT_KEY = 'predictedPropagationGrowth1Year';

export const TARGET_LEVELS = {
  severity4: 4,
  severity5: 5,
  severity6: CRITICAL_SEVERITY_LEVEL,
} as const;

export type PredictedTargetKey = keyof typeof TARGET_LEVELS;

// Ascending by severity level, which is the order of the rows and tooltip clauses.
const TARGET_KEYS: PredictedTargetKey[] = ['severity4', 'severity5', 'severity6'];

export type PredictedEstimate = {
  value: number;
  lowerBound?: number | null;
  upperBound?: number | null;
};

// One point on the prediction timeline. Called forecast, not horizon, to stay
// distinct from the Horizon product.
export type PredictedForecast = {
  years: number;
  months: number;
  growth: PredictedEstimate | null;
  // Cumulative: the chance of this level or worse. See toCumulativeTargets.
  targets: Partial<Record<PredictedTargetKey, PredictedEstimate>>;
};

export type PredictedPropagationSummary = {
  confidence: AtlasGqlHorizonDamagePredictedPropagationConfidence | null;
  createdAt: string | null;
  currentLevel: number | null;
  forecasts: PredictedForecast[];
  targetKeys: PredictedTargetKey[];
};

const toEstimate = (
  estimate?: AtlasGqlHorizonDamagePropagationEstimate | null
): PredictedEstimate | null => {
  const value = estimate?.value;
  if (isNil(value) || !Number.isFinite(value)) {
    return null;
  }
  return { value, lowerBound: estimate?.lowerBound, upperBound: estimate?.upperBound };
};

const monthsFromYears = (years: number): number => Math.round(years * 12);

// `severityN` is a distribution over where the Damage lands. The values sum to
// 100, and `growth` is 100 minus the chance it stays at the modelled severity.
// Triage asks how likely a Damage is to get at least this bad, so each level
// becomes a suffix sum.
//
// Bounds are per level and cannot be summed into a valid interval. They carry over
// only for a single term, or when the sum covers everything above the modelled
// severity and is therefore `growth`.
const toCumulativeTargets = (
  points: Partial<Record<PredictedTargetKey, PredictedEstimate>>,
  growth: PredictedEstimate | null,
  modelledSeverity?: number | null
): Partial<Record<PredictedTargetKey, PredictedEstimate>> =>
  TARGET_KEYS.reduce<Partial<Record<PredictedTargetKey, PredictedEstimate>>>((acc, key) => {
    const levels = TARGET_KEYS.filter(k => TARGET_LEVELS[k] >= TARGET_LEVELS[key]);
    const terms = levels.flatMap(k => points[k] ?? []);

    // Every level at or above this one must be present. A suffix sum that misses a
    // term understates the risk, which is worse than no number.
    if (!terms.length || terms.length !== levels.length) {
      return acc;
    }

    const value = terms.reduce((sum, term) => sum + term.value, 0);
    // By the model's own definition the suffix sum must equal `growth` here.
    // Require that before borrowing its interval, because bounds that contradict
    // the value they annotate are worse than no bounds.
    const isGrowth =
      !isNil(modelledSeverity) &&
      TARGET_LEVELS[key] === modelledSeverity + 1 &&
      !!growth &&
      Math.round(growth.value) === Math.round(value);
    const bounds = terms.length === 1 ? terms[0] : isGrowth ? growth : null;

    acc[key] = { value, lowerBound: bounds?.lowerBound, upperBound: bounds?.upperBound };
    return acc;
  }, {});

// Returns a number rather than a formatted string so the "%" stays inside the
// translated copy.
export const roundPercent = (value?: number | null): number | null => {
  if (isNil(value) || !Number.isFinite(value)) {
    return null;
  }
  return Math.round(Math.min(100, Math.max(0, value)));
};

// `percent` drives the color scale and comparisons. `label` is what the user sees.
export type DisplayPercent = { percent: number; label: string };

export const getDisplayPercent = (value?: number | null): DisplayPercent | null => {
  const percent = roundPercent(value);

  if (isNil(percent)) {
    return null;
  }

  // Under half a percent rounds to zero, and a flat "0%" reads as a promise of no
  // growth. An exact zero still says so plainly.
  const label = percent === 0 && !isNil(value) && value > 0 ? '<1' : String(percent);

  return { percent, label };
};

// Take the higher of the displayed attribute and the severity the model ran
// against. If the attributes were edited upward since then, the Damage already has
// that severity, and we must not promise growth to it.
export const getCurrentSeverityLevel = ({
  severity,
  isCritical,
  predictionSeverity,
}: {
  severity?: number | null;
  isCritical?: boolean | null;
  predictionSeverity?: number | null;
}): number | null => {
  if (isCritical) {
    return CRITICAL_SEVERITY_LEVEL;
  }

  const levels = [severity, predictionSeverity].filter(
    (level): level is number => !isNil(level) && Number.isFinite(level)
  );

  return levels.length ? Math.max(...levels) : null;
};

// Only severities above where the Damage already is. An unknown current severity
// shows all of them, because we cannot tell which ones it has passed.
export const getVisibleTargetKeys = (currentLevel: number | null): PredictedTargetKey[] =>
  isNil(currentLevel)
    ? [...TARGET_KEYS]
    : TARGET_KEYS.filter(key => TARGET_LEVELS[key] > currentLevel);

export const buildPredictedPropagationSummary = ({
  prediction,
  severity,
  isCritical,
}: {
  prediction?: AtlasGqlHorizonDamagePredictedPropagation | null;
  severity?: number | null;
  isCritical?: boolean | null;
}): PredictedPropagationSummary | null => {
  if (!prediction) {
    return null;
  }

  const forecasts = (prediction.predictions ?? [])
    .reduce<PredictedForecast[]>((acc, entry) => {
      const years = entry?.horizonYears;
      if (isNil(years) || !Number.isFinite(years)) {
        return acc;
      }

      const points = TARGET_KEYS.reduce<PredictedForecast['targets']>((targetAcc, key) => {
        const estimate = toEstimate(entry[key]);
        if (estimate) {
          targetAcc[key] = estimate;
        }
        return targetAcc;
      }, {});

      const growth = toEstimate(entry.growth);
      // Nothing predicted would render as a column of dashes.
      if (!growth && !Object.keys(points).length) {
        return acc;
      }

      const targets = toCumulativeTargets(points, growth, prediction.severity);

      acc.push({ years, months: monthsFromYears(years), growth, targets });
      return acc;
    }, [])
    .sort((a, b) => a.years - b.years);

  if (!forecasts.length) {
    return null;
  }

  const currentLevel = getCurrentSeverityLevel({
    severity,
    isCritical,
    predictionSeverity: prediction.severity,
  });

  // Drop levels with no derivable cumulative value, so no row is all dashes.
  const targetKeys = getVisibleTargetKeys(currentLevel).filter(key =>
    forecasts.some(forecast => forecast.targets[key])
  );

  return {
    confidence: prediction.confidence ?? null,
    createdAt: prediction.createdAt ?? null,
    currentLevel,
    forecasts,
    targetKeys,
  };
};

// Reads the server's primary Observation so the pill, the table column and the Details widget agree.
export const getPredictedPropagationSummaryForDamage = (
  damage?: AtlasGqlHorizonDamage | null
): PredictedPropagationSummary | null => {
  if (!damage) {
    return null;
  }

  const { primaryObservationGroup } = damage;
  const attrs = (primaryObservationGroup?.groupAttrs ?? {}) as Record<string, unknown>;
  const severity = attrs['Severity'];

  return buildPredictedPropagationSummary({
    prediction: primaryObservationGroup?.sourceObservation?.predictedPropagation,
    severity: typeof severity === 'number' ? severity : Number(severity),
    isCritical: primaryObservationGroup?.groupCritical,
  });
};

// Falls back to the longest forecast available. Consumers name the window they
// got, so a fallback is never mistaken for the requested one.
export const getForecast = (
  summary: PredictedPropagationSummary,
  years: number = PILL_FORECAST_YEARS
): PredictedForecast | undefined =>
  summary.forecasts.find(forecast => forecast.years === years) ??
  summary.forecasts[summary.forecasts.length - 1];

export type PillForecast = {
  percent: number;
  months: number;
  isFallback: boolean; // covers a window other than PILL_FORECAST_YEARS
};

// What the pill reports, or null when it would render nothing. Pills are scanned
// in bulk, so a fallback names its window instead of passing as a 12-month number.
export const getPillForecast = (
  summary?: PredictedPropagationSummary | null
): PillForecast | null => {
  if (!summary) {
    return null;
  }

  const forecast = getForecast(summary);
  const percent = roundPercent(forecast?.growth?.value);

  // A 0% growth chance reports nothing worth a pill. The Details widget still
  // reports the zeros for anyone who looks.
  if (!forecast || isNil(percent) || percent === 0) {
    return null;
  }

  return {
    percent,
    months: forecast.months,
    isFallback: forecast.years !== PILL_FORECAST_YEARS,
  };
};

// "2027-02" for the 6-month column. Counted from when the model ran, not from
// now, so the label stays true to what was predicted as the record ages.
export const getForecastDateLabel = (
  createdAt: string | null | undefined,
  years: number
): string | null => {
  if (!createdAt) {
    return null;
  }
  // UTC so the label does not shift with the reviewer's timezone.
  return formatMonthYear(moment.utc(createdAt).add(monthsFromYears(years), 'months'));
};

// Spelled out per severity rather than interpolating a translated severity name,
// so translators control agreement and word order. The keys must also stay string
// literals for the `usedKeys` test to see them.
export const getTargetClause = (key: PredictedTargetKey, percent: string, t: TFunction): string => {
  switch (key) {
    case 'severity4':
      return t(
        'damage.predictedPropagation.reachSeverity4',
        '{{percent}}% chance it will reach Severity 4 or worse',
        { percent }
      );
    case 'severity5':
      return t(
        'damage.predictedPropagation.reachSeverity5',
        '{{percent}}% chance it will reach Severity 5 or worse',
        { percent }
      );
    case 'severity6':
      return t(
        'damage.predictedPropagation.reachCritical',
        '{{percent}}% chance it will reach Critical Severity',
        { percent }
      );
  }
};

// Abbreviated because it heads a numeric column. The cell tooltip spells the
// cumulative meaning out in full. The `*Row` key names predate the transpose. The
// copy is unchanged, so they stay rather than churn six locale files.
export const getTargetLabel = (key: PredictedTargetKey, t: TFunction): string => {
  switch (key) {
    // Nothing is worse than Critical, so it needs no "or worse" marker.
    case 'severity6':
      return t('table.critical', 'Critical');
    case 'severity4':
      return t('damage.predictedPropagation.severityRow', 'Severity {{level}}+', { level: 4 });
    case 'severity5':
      return t('damage.predictedPropagation.severityRow', 'Severity {{level}}+', { level: 5 });
  }
};

export const getConfidenceLabel = (
  confidence: AtlasGqlHorizonDamagePredictedPropagationConfidence | null,
  t: TFunction
): string | null => {
  switch (confidence) {
    case AtlasGqlHorizonDamagePredictedPropagationConfidence.H:
      return t('damage.predictedPropagation.confidenceHigh', 'Confidence: High');
    case AtlasGqlHorizonDamagePredictedPropagationConfidence.M:
      return t('damage.predictedPropagation.confidenceMedium', 'Confidence: Medium');
    case AtlasGqlHorizonDamagePredictedPropagationConfidence.L:
      return t('damage.predictedPropagation.confidenceLow', 'Confidence: Low');
    default:
      return null;
  }
};

// Uses the locale's own list conventions ("A, and B" in English, "A y B" in
// Spanish) so no translated separator fragment is glued in by hand.
const formatClauseList = (clauses: string[], language: string): string => {
  try {
    return new Intl.ListFormat(language, { style: 'long', type: 'conjunction' }).format(clauses);
  } catch {
    return clauses.join(', ');
  }
};

// Complete clauses joined and interpolated into one composite key, so word order
// stays with translators.
export const buildGrowthTooltipSentence = (
  summary: PredictedPropagationSummary,
  t: TFunction,
  language: string
): string | null => {
  const forecast = getForecast(summary);
  const growth = getDisplayPercent(forecast?.growth?.value);

  if (!forecast || !growth) {
    return null;
  }

  const clauses = summary.targetKeys.reduce<string[]>((acc, key, index) => {
    const target = getDisplayPercent(forecast.targets[key]?.value);
    if (!target) {
      return acc;
    }
    // The lowest level restates `growth`. Only the first clause is dropped, so a
    // coincidental match further up is still reported. The comparison uses the
    // rounded number, because two different values can share a "<1" label.
    if (index === 0 && target.percent === growth.percent) {
      return acc;
    }
    acc.push(getTargetClause(key, target.label, t));
    return acc;
  }, []);

  if (!clauses.length) {
    return t(
      'damage.predictedPropagation.growthOnlyTooltip',
      '{{growth}}% chance it will grow within {{count}} months to a higher Severity',
      { count: forecast.months, growth: growth.label }
    );
  }

  return t(
    'damage.predictedPropagation.growthTooltip',
    '{{growth}}% chance it will grow within {{count}} months to a higher Severity - {{breakdown}}',
    { count: forecast.months, growth: growth.label, breakdown: formatClauseList(clauses, language) }
  );
};
